import { z } from "zod";

/**
 * V4 enums — UPPERCASE to match Postgres storage (packages/db) exactly.
 * Single source of truth for the wire format. Both apps/api and apps/web
 * import from here.
 */

export const JobState = z.enum([
  "NEW",
  "IN_PROGRESS",
  "FINISHED",
  "REVIEWED",
  "CANCELLED",
  "PAUSED",
]);
export type JobState = z.infer<typeof JobState>;

export const UserRole = z.enum(["DRIVER", "DISPATCHER", "OFFICE"]);
export type UserRole = z.infer<typeof UserRole>;

export const JobType = z.enum(["S_O", "E_R", "D_O", "R_O"]);
export type JobType = z.infer<typeof JobType>;

export const PhotoKind = z.enum([
  "TICKET_WEIGHT",
  "DELIVERY_PROOF",
  "DAMAGE",
  "OTHER",
  "LOAD",
]);
export type PhotoKind = z.infer<typeof PhotoKind>;

/**
 * LoadDecision — office routing decision on a load-photo-documented job
 * (SPEC-26). LANDFILL = dump the load; FACILITY_METAL = bring to our facility
 * to pull the metal. Mirrors the jobs.load_decision DB CHECK (0014).
 */
export const LoadDecision = z.enum(["LANDFILL", "FACILITY_METAL"]);
export type LoadDecision = z.infer<typeof LoadDecision>;

export const PhotoSource = z.enum(["CAMERA", "GALLERY"]);
export type PhotoSource = z.infer<typeof PhotoSource>;

export const CancellationReason = z.enum([
  "CUSTOMER_CANCELLED",
  "WEATHER",
  "TRUCK_BREAKDOWN",
  "DRIVER_UNAVAILABLE",
  "OTHER_WITH_NOTE",
]);
export type CancellationReason = z.infer<typeof CancellationReason>;

export const EditReason = z.enum([
  "WEIGHT_CORRECTION",
  "AMOUNT_CORRECTION",
  "WRONG_CUSTOMER",
  "WRONG_PROPERTY",
  "OTHER",
  "BREAK_GLASS",
]);
export type EditReason = z.infer<typeof EditReason>;

// ── Human-readable labels for the UI ────────────────────────────────────────

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  S_O: "S/O (Switch Out)",
  E_R: "E/R (Empty & Return)",
  D_O: "D/O (Delivery Only)",
  R_O: "R/O (Removal Only)",
};

export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  CUSTOMER_CANCELLED: "Customer cancelled",
  WEATHER: "Weather",
  TRUCK_BREAKDOWN: "Truck breakdown",
  DRIVER_UNAVAILABLE: "Driver unavailable",
  OTHER_WITH_NOTE: "Other (note required)",
};

export const EDIT_REASON_LABELS: Record<EditReason, string> = {
  WEIGHT_CORRECTION: "Weight correction",
  AMOUNT_CORRECTION: "Amount correction",
  WRONG_CUSTOMER: "Wrong customer",
  WRONG_PROPERTY: "Wrong property",
  OTHER: "Other",
  BREAK_GLASS: "Break-glass override",
};

/** Reference-data edit reasons — per-entity subsets of EditReason. */
export type CustomerEditReason = Extract<EditReason, "WRONG_CUSTOMER" | "OTHER">;
export type PropertyEditReason = Extract<EditReason, "WRONG_PROPERTY" | "OTHER">;

